import {createAsyncThunk} from '@reduxjs/toolkit'
import {SignInWithRedirectInput} from 'aws-amplify/auth'
import {keys, reject} from 'ramda'

import {UserTier} from '../../offer/business/privateDeals'
import {WAS_PAYMENT_PAGE_AUTHENTICATED} from '../../searchParams/constants'
import {setLastLoginUrl} from '../business/persistData'
import {
  socialLoginService,
  updateUserAttributesService,
  upgradeUserTier
} from '../services'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  paymentPageSourceComponents,
  SourceComponentType
} from '../types/Auth'
import {CognitoError, UserAttributes} from '../types/Cognito'
import {Connections} from './selectors.shared'

export interface SendMessageError {
  error: string
  error_description: string
}

export interface CognitoDDError {
  type: string
  error: CognitoError | string
}

/**
 * Authorize users via Cognito sdk
 */
interface makeSocialLoginArgs {
  connection: Connections
  unlockHotelId: string
  // Nullable, not optional: a caller with no origin has to say `null`.
  sourceComponent: SourceComponentType
  callbackUrl?: string | null
  flow: AuthenticationFlows | null
  analyticsData?: Record<string, unknown>
}

/**
 * Make a social login
 * @param args - The arguments for the social login
 * @param args.connection - The connection to use
 * @param args.unlockHotelId - The hotel id to unlock
 * @param args.sourceComponent - The source component
 */
export const makeSocialLogin = createAsyncThunk<
  unknown,
  makeSocialLoginArgs,
  {rejectValue: CognitoError}
>(
  'makeSocialLogin',
  async ({
    connection,
    unlockHotelId,
    sourceComponent,
    callbackUrl,
    flow,
    analyticsData
  }) => {
    const returningUrl = new URL(callbackUrl || window.location.href)

    if (unlockHotelId) {
      returningUrl.searchParams.set('hotelId', unlockHotelId)
    }

    if (paymentPageSourceComponents.includes(sourceComponent)) {
      returningUrl.searchParams.set(WAS_PAYMENT_PAGE_AUTHENTICATED, 'true')
    }

    setLastLoginUrl(returningUrl.toString())

    // `partner-OIDC` is the federated Identity Provider name registered
    // on the Cognito User Pool. It is shared across every brand whose
    // `auth.hasOidcProvider` capability resolves true — onboarding a
    // new OIDC partner reuses the same federation, no per-brand override.
    const connectionMapping: Record<string, string> = {
      [Connections.Google]: 'Google',
      [Connections.Facebook]: 'Facebook',
      [Connections.Apple]: 'Apple',
      [Connections.OIDCFederation]: 'partner-OIDC'
    }

    const authenticationMethodMapping: Record<
      Connections,
      AuthenticationMethod
    > = {
      [Connections.Google]: AuthenticationMethod.Google,
      [Connections.Facebook]: AuthenticationMethod.Facebook,
      [Connections.Apple]: AuthenticationMethod.Apple,
      [Connections.OIDCFederation]: AuthenticationMethod.OIDCFederation
    }

    const providerName = connectionMapping[connection]
    const provider =
      connection === Connections.OIDCFederation
        ? {custom: providerName}
        : (providerName as SignInWithRedirectInput['provider'])

    return socialLoginService(provider, {
      sourceComponent: sourceComponent as SourceComponentType,
      authenticationMethod: authenticationMethodMapping[connection],
      flow,
      analyticsData
    })
  }
)

/**
 * Updates users' Cognito attributes
 */
export const updateUserAttributes = createAsyncThunk<
  UserAttributes,
  UserAttributes,
  {rejectValue: unknown}
>('updateUserAttributes', async (attributes, {rejectWithValue}) => {
  try {
    const response = await updateUserAttributesService(attributes)

    const rejectedValues = reject(
      (attribute: keyof UserAttributes) => response[attribute]?.isUpdated,
      keys(attributes)
    )

    if (rejectedValues?.length === 0) {
      return attributes
    }

    return rejectWithValue(rejectedValues)
  } catch (error) {
    return rejectWithValue(error as Error)
  }
})

/**
 * Upgrades users to plus
 */
export const upgradeUserToPlus = createAsyncThunk<
  unknown,
  void,
  {
    rejectValue: {
      error: unknown
    }
  }
>('upgradeUserToPlus', async (_, {rejectWithValue, fulfillWithValue}) => {
  try {
    const response = await upgradeUserTier(UserTier.Plus)

    return fulfillWithValue(response)
  } catch (error) {
    return rejectWithValue(error as {error: unknown})
  }
})
